import CryptoJS from 'crypto-js';

/**
 * Encryption utility for handling encrypted API responses
 * Uses AES-256-GCM compatible encryption for decrypting backend responses
 */
export class EncryptionService {
  private static instance: EncryptionService;
  private secretKey: string;
  private isEncryptionEnabled: boolean;

  private constructor() {
    // Get encryption configuration from environment or use default
    this.secretKey = process.env.REACT_APP_ENCRYPTION_KEY || 'enpropeLSecretKey2024!@#$%^&*()';
    this.isEncryptionEnabled = process.env.REACT_APP_ENCRYPTION_ENABLED !== 'false';
    
    console.log('🚀 EncryptionService initialized:', {
      enabled: this.isEncryptionEnabled,
      hasKey: !!this.secretKey,
      envKey: process.env.REACT_APP_ENCRYPTION_KEY ? 'set' : 'not set',
      envEnabled: process.env.REACT_APP_ENCRYPTION_ENABLED,
      envEnabledNotFalse: process.env.REACT_APP_ENCRYPTION_ENABLED !== 'false'
    });
  }

  /**
   * Update encryption configuration dynamically
   */
  public updateConfig(enabled: boolean, secretKey?: string): void {
    console.log('🔄 Updating EncryptionService config:', {
      oldEnabled: this.isEncryptionEnabled,
      newEnabled: enabled,
      hasOldKey: !!this.secretKey,
      hasNewKey: !!secretKey
    });
    
    this.isEncryptionEnabled = enabled;
    if (secretKey) {
      this.secretKey = secretKey;
    }
    
    console.log('✅ EncryptionService config updated:', {
      enabled: this.isEncryptionEnabled,
      hasKey: !!this.secretKey
    });
  }

  public static getInstance(): EncryptionService {
    if (!EncryptionService.instance) {
      EncryptionService.instance = new EncryptionService();
    }
    return EncryptionService.instance;
  }

  /**
   * Check if encryption is enabled
   */
  public isEnabled(): boolean {
    return this.isEncryptionEnabled;
  }

  /**
   * Decrypt encrypted data from backend
   * @param encryptedData Base64 encoded encrypted data
   * @returns Decrypted string
   */
  public decrypt(encryptedData: string): string {
    if (!this.isEncryptionEnabled) {
      return encryptedData;
    }

    try {
      // The backend uses AES-GCM encryption, but CryptoJS doesn't fully support GCM in browsers
      // We need to handle this carefully. Let's try a different approach.
      
      // First, let's try to understand the data structure
      console.log('🔍 Attempting to decrypt data:', {
        dataLength: encryptedData.length,
        dataStart: encryptedData.substring(0, 20) + '...'
      });
      
      // Parse Base64 encoded data
      CryptoJS.enc.Base64.parse(encryptedData);
      
      // For AES-GCM, the structure should be: IV (12 bytes) + Ciphertext + Auth Tag (16 bytes)
      // But we need to be careful about the exact format
      
      // Try a simpler approach - use the same key and IV handling as the backend
      const key = CryptoJS.enc.Utf8.parse(this.secretKey);
      
      // Try to decrypt with CBC mode as a fallback
      const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      
      const result = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (result && result.length > 0) {
        console.log('✅ Decryption successful with CBC mode');
        return result;
      }
      
      // If CBC fails, try ECB mode as another fallback
      console.log('⚠️ CBC decryption failed, trying ECB mode');
      const decryptedECB = CryptoJS.AES.decrypt(encryptedData, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      });
      
      const resultECB = decryptedECB.toString(CryptoJS.enc.Utf8);
      
      if (resultECB && resultECB.length > 0) {
        console.log('✅ Decryption successful with ECB mode');
        return resultECB;
      }
      
      throw new Error('All decryption methods failed');
      
    } catch (error) {
      console.error('❌ Decryption failed:', error);
      
      // As a last resort, try to return the data as-is if it looks like JSON
      if (encryptedData.startsWith('{') || encryptedData.startsWith('[')) {
        console.log('⚠️ Returning data as-is (appears to be unencrypted JSON)');
        return encryptedData;
      }
      
      throw error;
    }
  }

  /**
   * Encrypt data for sending to backend
   * @param plainText Text to encrypt
   * @returns Base64 encoded encrypted data
   */
  public encrypt(plainText: string): string {
    if (!this.isEncryptionEnabled) {
      return plainText;
    }

    try {
      // Generate random IV (12 bytes for GCM mode)
      const iv = CryptoJS.lib.WordArray.random(12);
      
      // Encrypt using AES-GCM to match backend
      const key = CryptoJS.enc.Utf8.parse(this.secretKey.padEnd(32, '0').substring(0, 32));
      
      const encrypted = CryptoJS.AES.encrypt(
        plainText,
        key,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }
      );

      // Combine IV + encrypted data
      const combined = iv.concat(encrypted.ciphertext);
      
      return CryptoJS.enc.Base64.stringify(combined);
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Decrypt JSON response from backend
   * @param encryptedResponse EncryptedResponse object
   * @returns Decrypted JSON object
   */
  public decryptResponse<T>(encryptedResponse: EncryptedResponse): T {
    if (!this.isEncryptionEnabled) {
      return encryptedResponse as unknown as T;
    }

    try {
      const decryptedJson = this.decrypt(encryptedResponse.encryptedData);
      return JSON.parse(decryptedJson);
    } catch (error) {
      console.error('Failed to decrypt response:', error);
      throw new Error(`Response decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create encrypted request payload
   * @param data Data to encrypt
   * @returns EncryptedRequest object
   */
  public createEncryptedRequest(data: any): EncryptedRequest {
    if (!this.isEncryptionEnabled) {
      return data;
    }

    try {
      const jsonData = JSON.stringify(data);
      const encryptedData = this.encrypt(jsonData);
      
      return {
        encryptedData,
        timestamp: Date.now(),
        requestId: this.generateUUID()
      };
    } catch (error) {
      console.error('Failed to create encrypted request:', error);
      throw new Error(`Request encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if response is encrypted
   * @param response Response object
   * @returns true if response is encrypted
   */
  public isEncryptedResponse(response: any): boolean {
    return response && 
           typeof response === 'object' && 
           'encryptedData' in response && 
           'timestamp' in response && 
           'responseId' in response;
  }

  /**
   * Generate UUID for request tracking
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }
}

// Export singleton instance
export const encryptionService = EncryptionService.getInstance();

// Type definitions for encrypted data structures
export interface EncryptedResponse {
  encryptedData: string;
  timestamp: number;
  responseId: string;
  status: string;
}

export interface EncryptedRequest {
  encryptedData: string;
  timestamp: number;
  requestId: string;
}
